import {gql} from "@apollo/client";

import {CheckoutContext} from "@/components/context/CheckoutContext.tsx";
import {PlanDefinition, PlanInfo, PlanLimits} from "@/lib/types/accounts.ts";
import {AccountStats, BillingData, CalculatedBillingData, Invoice} from "@/lib/types/billing.ts";
import {GenericResponse, QueryError} from "@/lib/types/common.ts";
import {SubscriptionPack, SubscriptionPackTier} from "@/pages/billing/v2/types.ts";

export interface Plan {
  name: string;
  limits: PlanDefinition;
}

export interface PurchaseRequestResponse {
  purchaseRequest: GenericResponse;
}

export const GQL_PURCHASE_REQUEST = gql`
  mutation PurchaseRequest($addedValues: [AddedValue!]!) {
    purchaseRequest(addedValues: $addedValues) {
      success
      errors
    }
  }
`;

export interface PurchaseAddonsResponse {
  purchaseAddOns: GenericResponse;
}

export const GQL_PURCHASE_ADDONS = gql`
  mutation PurchaseAddOns($addedValues: [AddedValue!]!) {
    purchaseAddOns(addedValues: $addedValues) {
      success
      errors
    }
  }
`;

export interface AccountStatsResults {
  accountStats: {
    items: AccountStats[];
  };
}

export const GQL_ACCOUNT_STATS = gql`
  query AccountStats {
    accountStats {
      items {
        name
        max_monitoring_groups
        max_monitoring_group_level
        sms_credits
        services
        sms
        sms_and_phone_alerts
        sp_sms
        phonecall
        api_checks
        rum_checks
        custom_checks
        group_checks
        cloudstatus_checks
        pagespeed_checks
        transaction_checks
        basic_checks
        advanced_checks
        multi_users
        dashboards
        statuspages
        statuspages_subscriptions
        reports
        scheduled_reports
        rum_requests
        private_locations
        main_account_plan
        account {
          id
          name
          type
          plan
          timezone
          free_trial_expires_at
          has_can_purchase_feature
          limit_check_frequency
          limit_monitoring_group_level
          limit_monitoring_groups
          limit_private_locations
          limit_history
          enable_audit_log
          enable_string_matching
          enable_api_access
          enable_sso
          enable_advanced_features
          enable_enterprise_features
          enable_international_sms
          feature_flags {
            name
          }
        }
        locations
        statuspages_external_user
      }
    }
  }
`;

export interface CheckoutContextResult {
  checkoutContext: CheckoutContext;
}

export const GQL_CHECKOUT_CONTEXT = gql`
  query CheckoutContext {
    checkoutContext {
      braintreeToken
      hasActiveSubscription
      isSubscriptionPastDue
      hasActivePremiumFeatures
      nextBillingDate
      billingAmount
      paymentMethod {
        pm_type
        type
        img
        suffix
        expiration_date
        is_expired
        email
      }
      billingData {
        ...BillingDataFields
      }
    }
  }
`;

export interface BillingDataResult {
  billingData: BillingData;
}

export const GQL_BILLING_DATA = gql`
  query BillingData {
    billingData {
      ...BillingDataFields
    }
  }
`;

export interface SetPaymentMethodResponse {
  setPaymentMethod: GenericResponse;
}

export const GQL_SET_PAYMENT_METHOD = gql`
  mutation setPaymentMethod($paymentMethodData: PaymentMethodInput!) {
    setPaymentMethod(paymentMethodData: $paymentMethodData) {
      success
      errors
    }
  }
`;

export interface CalculatedBillingDataResponse {
  calculateBillingData: CalculatedBillingData | QueryError;
}

export const GQL_CALCULATE_BILLING_DATA = gql`
  query CalculateBillingData(
    $plan: BillingPlan
    $billingPeriod: BillingPeriod
    $addedItems: [AddedValue!]
    $promoCode: String
    $skipApplicabilityCheck: Boolean
  ) {
    calculateBillingData(
      plan: $plan
      billingPeriod: $billingPeriod
      addedItems: $addedItems
      promoCode: $promoCode
      skipApplicabilityCheck: $skipApplicabilityCheck
    ) {
      ... on ErrorResponse {
        status
        message
      }
      ... on CalculatedBillingData {
        added_cost
        added_cost_full
        base_plan
        base_price
        base_price_subtotal
        discounts {
          title
          percent
          value
          mini_quote
          is_promo
          recurring
          highlight
        }
        monthly_subtotal
        price_monthly
        total_due_today
        units_prices {
          name
          price
        }
      }
    }
  }
`;

export interface PlanInfoResult {
  planInfo: PlanInfo;
}

export const FGM_PLAN_INFO = gql`
  fragment PlanInfoFields on PlanInfo {
    price_monthly
    price_annual
    isOverLimit
    name
    limits {
      limit_api_checks {
        ...PlanLimitFields
      }
      limit_basic_checks {
        ...PlanLimitFields
      }
      limit_cloudstatus_checks {
        ...PlanLimitFields
      }
      limit_custom_checks {
        ...PlanLimitFields
      }
      limit_group_checks {
        ...PlanLimitFields
      }
      limit_history {
        ...PlanLimitFields
      }
      limit_monitoring_group_level {
        included
        used
      }
      limit_monitoring_groups {
        ...PlanLimitFields
      }
      limit_multi_users {
        ...PlanLimitFields
      }
      limit_pagespeed_checks {
        ...PlanLimitFields
      }
      limit_phonecalls {
        ...PlanLimitFields
      }
      limit_sms_and_phone_alerts {
        ...PlanLimitFields
      }
      limit_private_locations {
        ...PlanLimitFields
      }
      limit_rum_checks_requests_per_month {
        ...PlanLimitFields
      }
      limit_sms {
        ...PlanLimitFields
      }
      limit_statuspage_subscriptions {
        ...PlanLimitFields
      }
      limit_statuspages {
        ...PlanLimitFields
      }
      limit_statuspages_external_user {
        ...PlanLimitFields
      }
      limit_transaction_checks {
        ...PlanLimitFields
      }
      limit_check_frequency {
        included
        used
      }
    }
  }
`;

export const FGM_PLAN_LIMIT_FIELDS = gql`
  fragment PlanLimitFields on PlanLimit {
    included
    limit
    used
    exhausted
    allowed
    bundleSize
  }
`;

export const GQL_PLAN_INFO = gql`
  query planInfo($plan: BillingPlan, $ignoreAccountPlan: Boolean) {
    planInfo(plan: $plan, ignoreAccountPlan: $ignoreAccountPlan) {
      ...PlanInfoFields
    }
  }
`;

export type CheckResetItem = {
  id: number;
  name: string;
  url: string;
};

export type CheckResetList = {
  checks: CheckResetItem[];
  total: number;
};

export type CheckResetListResponse = {
  checkResetList: CheckResetList;
};

export const GQL_CHECK_RESET_LIST_QUERY = gql`
  query CheckResetList($plan: BillingPlan) {
    checkResetList(plan: $plan) {
      total
      checks {
        id
        name
        url
      }
    }
  }
`;

export interface ResetLocationsResponse {
  resetLocations: GenericResponse;
}

export const GQL_RESET_LOCATIONS = gql`
  mutation resetLocations($plan: BillingPlan!) {
    resetLocations(plan: $plan) {
      success
      errors
    }
  }
`;

export interface UpdateSubscriptionResponse {
  updateSubscription: GenericResponse;
}

export const GQL_UPDATE_SUBSCRIPTION = gql`
  mutation updateSubscription(
    $plan: BillingPlan!
    $billingPeriod: BillingPeriod!
    $addedValues: [AddedValue!]
    $promoCode: String
  ) {
    updateSubscription(plan: $plan, billingPeriod: $billingPeriod, addedValues: $addedValues, promoCode: $promoCode) {
      success
      errors
    }
  }
`;

export interface UpdateSubscriptionV2Response {
  updateSubscriptionV2: GenericResponse;
}

export const GQL_UPDATE_SUBSCRIPTION_V2 = gql`
  mutation updateSubscriptionV2($billingPeriod: BillingPeriod!, $selectedTierIds: [String!]!) {
    updateSubscriptionV2(billingPeriod: $billingPeriod, selectedTierIds: $selectedTierIds) {
      success
      errors
    }
  }
`;

export interface InstantCheckoutV2Response {
  instantCheckoutV2: GenericResponse;
}

export const GQL_INSTANT_CHECKOUT_V2 = gql`
  mutation instantCheckoutV2($selectedTierId: String!, $originalTrigger: String) {
    instantCheckoutV2(selectedTierId: $selectedTierId, originalTrigger: $originalTrigger) {
      success
      errors
    }
  }
`;

export interface PurchaseRequestV2Response {
  purchaseRequestV2: GenericResponse;
}

export const GQL_PURCHASE_REQUEST_V2 = gql`
  mutation PurchaseRequestV2($selectedTierId: String!) {
    purchaseRequestV2(selectedTierId: $selectedTierId) {
      success
      errors
    }
  }
`;

export interface CustomQuoteRequestResponse {
  customRequest: GenericResponse;
}

export const GQL_CUSTOM_QUOTE_REQUEST = gql`
  mutation updateSubscription($data: CustomQuoteRequestInput!) {
    customQuoteRequest(data: $data) {
      success
      errors
    }
  }
`;

export interface CancelSubscriptionResponse {
  cancelSubscription: GenericResponse;
}

export const GQL_CANCEL_SUBSCRIPTION = gql`
  mutation cancelSubscription($churnReasonDetails: String!) {
    cancelSubscription(churnReasonDetails: $churnReasonDetails) {
      success
      errors
    }
  }
`;

export type InvoicesList = {
  items: Invoice[];
  total: number;
};

export type InvoicesListResponse = {
  invoicesList: InvoicesList;
};

export const GQL_INVOICES_LIST = gql`
  query InvoicesList($offset: Int, $limit: Int, $sort: String) {
    invoicesList(offset: $offset, limit: $limit, sort: $sort) {
      total
      items {
        id
        transaction_id
        created_at
        amount
        is_successful
        type
        legacy_plan
        status
        source
        period_start
        period_end
        payment_instrument_type
        processor_response_code
        processor_response_type
        processor_response_text
        credit_card_type
        credit_card_slug
        credit_card_suffix
        paypal_account
      }
    }
  }
`;

export interface InvoiceDetailsResponse {
  invoiceDetails: Invoice;
}

export const GQL_INVOICE_DETAILS = gql`
  query InvoiceDetails($id: String!) {
    invoiceDetails(id: $id) {
      id
      transaction_id
      created_at
      amount
      is_successful
      type
      legacy_plan
      status
      source
      period_start
      period_end
      payment_instrument_type
      processor_response_code
      processor_response_type
      processor_response_text
      credit_card_type
      credit_card_slug
      credit_card_suffix
      paypal_account
    }
  }
`;

interface SubscriptionPackItems {
  items: SubscriptionPack[];
}

export interface SubscriptionPacksResponse {
  subscriptionPacks: SubscriptionPackItems;
}

export const GQL_SUBSCRIPTION_PACKS = gql`
  query SubscriptionPacks {
    subscriptionPacks {
      items {
        pack_id
        title
        default_values {
          tier_id
          is_active
          is_forced
          is_mandatory
        }
        lines
        tiers {
          tier_id
          pack_id
          price_monthly
          price_annual
          limits {
            limit_field
            limit_name
            limit_used
            value
            value_alt
          }
          is_current
          is_custom_price
          is_custom_limits
        }
      }
    }
  }
`;

export interface ExhaustedLimit {
  limit_field: keyof PlanLimits;
  current_limit: number;
}

export interface CheckSubscriptionLimitResponse {
  checkSubscriptionLimit: {
    current_limit: number;
    limit_name: string;
    is_exhausted: boolean;
    pending_purchase_request_at: string;
    pack_lines: string[];
    current_tier?: SubscriptionPackTier;
    next_tier?: SubscriptionPackTier;
    exhausted_additional_limits: ExhaustedLimit[];
  };
}

export enum CheckLimitField {
  BASIC_CHECKS = "LIMIT_BASIC_CHECKS",
  ADVANCED_CHECKS = "LIMIT_ADVANCED_CHECKS",
  GROUP_CHECKS = "LIMIT_GROUP_CHECKS",
}

export interface AdditionalLimitInput {
  limit_field: CheckLimitField;
  requested_value: number;
}

export const GQL_CHECK_SUBSCRIPTION_LIMIT = gql`
  query CheckSubscriptionLimit(
    $limitField: String
    $serviceType: String
    $requestedValue: Int
    $requestedIncrement: Int
    $additionalLimits: [AdditionalLimitInput!]
  ) {
    checkSubscriptionLimit(
      limitField: $limitField
      serviceType: $serviceType
      requestedValue: $requestedValue
      requestedIncrement: $requestedIncrement
      additionalLimits: $additionalLimits
    ) {
      current_limit
      limit_name
      is_exhausted
      pending_purchase_request_at
      pack_lines
      exhausted_additional_limits {
        limit_field
        current_limit
      }
      current_tier {
        tier_id
        pack_id
        price_monthly
        price_annual
        limits {
          limit_field
          limit_name
          value
          value_alt
        }
      }
      next_tier {
        tier_id
        pack_id
        price_monthly
        price_annual
        limits {
          limit_field
          limit_name
          value
          value_alt
        }
      }
    }
  }
`;
